export interface Employee {
  id: number;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  status: number;
  createdBy: CreatedBy;
  updatedBy: UpdatedBy;
  code: string;
  email: string;
  codeRef?: string;
  phoneCode?: string;
  phoneNumber?: string;
  firstName: string;
  lastName: string;
  fullName: string;
  dateOfBirth?: string;
  gender: string;
  title: string;
  titleCode: string;
  positionCode?: string;
  positionTitle?: string;
  paths: number[];
  materialStatus?: string;
  personalEmail?: string;
  firstWorkingDate?: string;
  lastWorkingDate?: string;
  employmentType?: string;
  employmentStatus: string;
  isExpat: boolean;
  probationStartDate?: string;
  probationPeriod?: string;
  probationEndDate?: string;
  contractNo?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractNoticePeriod?: number;
  costCenter?: string;
  payrollTemplateId?: string;
  payrollTemplate?: unknown;
  tenantId: number;
  organizationId: number;
  userId: number;
  departmentId: number;
  hrbpId?: string;
  branchId?: string;
  workLevelId?: string;
  contractTypeId?: string;
  organization: Organization;
  department: Department;
  manager: Manager;
  hrbp?: Manager;
  workLevel?: WorkLevel;
  contractType?: ContractType;
  branch?: Branch;
  taxCode?: string;
  oldTaxCode?: string;
  taxObjectType?: string;
  isAuthorizeTaxFinalization?: boolean;
  compensationId?: string;
  leaves?: Record<string, number>;
  passcode?: string;
  isWorkingAfterRetirement?: boolean;
}

interface Manager {
  id: number;
  code: string;
  fullName: string;
  title: string;
}

interface Department {
  id: number;
  name: string;
  code: string;
  paths: number[];
}

interface Branch {
  id: number;
  name: string;
  code: string;
  paths: number[];
}

interface Organization {
  id: number;
  name: string;
  code: string;
}

interface ContractType {
  id: number;
  name: string;
  nativeName: string;
  code: string;
}

interface WorkLevel {
  id: number;
  name: string;
  nativeName: string;
  code: string;
}

interface UpdatedBy {
  userId: number;
  fullName: string;
  employeeId?: number;
}

interface CreatedBy {
  userId: number;
  fullName: string;
}

export interface EmployeeListRequest {
  period?: string | null;
  page?: number;
  limit?: number;
  keyword?: string | null;
  status?: number | null;
  ids?: number[];
  code?: string | null;
  email?: string | null;
  phoneNumber?: string | null;
  fullName?: string | null;
  gender?: string | null;
  titleCode?: string | null;
  departmentId?: string | null;
  titleCodes?: number[] | null;
  departmentIds?: number[] | null;
  workLevelId?: number | null;
  workLevelIds?: number[] | null;
  employmentTypes?: string[] | null;
  employmentStatuses?: string[] | null;
  month?: number | null;
  year?: number | null;
  filterWithUnLock?: boolean;
  payrollTemplateIds?: string[] | null;
  templateIds?: string[] | null;
  templateId?: string | null;
  templatePeriod?: string | null;
  cycleId?: string | null;
  lockType?: "TIME_ATTENDANCE" | "OT" | "LEAVE" | "PAYROLL";
  isGetPayroll13th?: boolean;
  isGetKpiIndividual?: boolean;
  isGetKpiDepartment?: boolean;
}

export interface EmployeeCreateRequest {
  code: string;
  email: string;
  phoneCode?: string;
  phoneNumber?: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender: string;
  titleCode: string;
  codeRef?: string;
  title?: string;
  materialStatus?: string;
  costCenter?: string;
  personalEmail?: string;
  firstWorkingDate?: string;
  lastWorkingDate?: string;
  employmentType?: string;
  employmentStatus?: string;
  contractNo?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractNoticePeriod?: number;
  probationStartDate?: string;
  probationPeriod?: number;
  probationEndDate?: string;
  // email?: string;
  managerId?: number;
  departmentId: number;
  isExpat: boolean;
  branchId?: number;
  workLevelId?: number;
  contractTypeId?: string;
  hrbpId?: number;
  taxCode?: string;
  oldTaxCode?: string;
  taxObjectType?: string;
  isAuthorizeTaxFinalization?: boolean;
  passcode?: string;
  isWorkingAfterRetirement?: boolean;
}

export interface EmployeeUpdateRequest extends EmployeeCreateRequest {
  id: string;
}

export interface EmployeeListResponse extends RestAPIMetaResponse {
  items: Array<Employee>;
}

export interface EmployeeImport {
  code: string;
  phoneCode?: string;
  phoneNumber?: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  gender: string;
  titleCode: string;
  title?: string;
  materialStatus?: string;
  personalEmail?: string;
  firstWorkingDate?: string;
  lastWorkingDate?: string;
  employmentType?: string;
  employmentStatus?: string;
  contractNo?: string;
  contractStartDate?: string;
  contractEndDate?: string;
  contractNoticePeriod?: number;
  probationStartDate?: string;
  probationPeriod?: number;
  probationEndDate?: string;
  taxCode?: string;
  taxObjectType?: string;
  isExpat?: boolean;
  isAuthorizeTaxFinalization: boolean;
  rowId: number;
  email: string;
  departmentCode: string;
  managerCode?: string;
  hrbpCode?: string;
  branchCode?: string;
  workLevelCode?: string;
  contractTypeCode?: string;
}

export interface EmployeeWorkingHistory {
  month: number;
  year: number;
  templateName: null;
  templateNativeName: null;
  empTypeCode: string;
  empDeptName: string;
  empOfficeName: string;
  empTitleName: string;
  compTotalTakehome: number;
  empCompensationName: null;
  empCompensationNativeName: null;
  empManager: EmpManager;
  isCurrent: boolean;
}

interface EmpManager {
  id: number;
  code: string;
  email: string;
  fullName: string;
  title: string;
  titleCode: string;
}

export interface EmployeeWorkingHistoryResponse extends RestAPIMetaResponse {
  items: Array<EmployeeWorkingHistory>;
}

export interface EmployeeImportRequest {
  employees: EmployeeImport;
  fileName?: string;
}

interface EmployeeImportItem {
  rowId: number;
  id?: string;
  message: string;
}

export interface EmployeeImportResponse {
  items: EmployeeImportItem[];
}

export enum EmployeeType {
  FullTime = "FTE",
  PartTime = "PTE",
  Internship = "ITE",
  Apprenticed = "APE",
  Temporary = "TEM",
  Contractors = "CON",
  Trainee = "TTP",
}

export enum EmployeeGender {
  Male = "Male",
  Female = "Female",
  Other = "Other",
}

export enum EmployeeIsExpat {
  VietNam = "VietNam",
  Foreign = "Foreign",
}

export enum EmploymentStatus {
  Probationary = "Probationary",
  Termination = "Termination",
  Working = "Working",
  Maternity = "Maternity",
  LongTermLeave = "LongTermLeave",
}

export enum EmployeeTaxObjectType {
  Resident = "Resident",
  NonResident = "NonResident",
}

export enum EmployeeMaterialStatus {
  Single = "Single",
  Married = "Married",
  Divorced = "Divorced",
  Widowed = "Widowed",
  Other = "Other",
}
